import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import type { RootState } from '@/state/store';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { useOnboardingForm } from '@/components/smartai/ComponentOnboarding/useOnboardingForm';
import type { FieldConfig } from '@/components/smartai/ComponentOnboarding/useOnboardingForm';
import {
  useStartManualSession,
  useEndManualSession,
  useMarkManualSessionResult,
  useFetchTestCasesFromJira,
  useSubmitSessionTestCases,
  type ManualTestCase,
  type ManualTestCaseGroup,
  type StartSessionResponse,
  type EndSessionResponse,
  type SubmitSessionTestCasesResponse,
  getEndSessionDisplayStats,
  buildStartManualSessionRequest,
  getManualTestingPlatformBackendUrl,
  resolveManualTestingTargetAppUrl,
} from '@/components/smartai/api/manualTesting';
import { getApiErrorMessage } from '@/utils';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import {
  mtView,
  mtPageTitle,
  mtPageSub,
  mtCardWrap,
  mtCardTitle,
  mtError,
  mtBtnRow,
  mtBtnRowTop,
  mtModeGrid,
  mtModeCard,
  mtModeCardActive,
  mtModeTitle,
  mtModeDesc,
  mtWarn,
  mtWarnText,
  mtDetailGrid,
  mtDetailLabel,
  mtTcTree,
  mtTcSuite,
  mtTcSuiteHeader,
  mtTcSuiteCount,
  mtTcRow,
  mtTcRowBtn,
  mtTcRowPass,
  mtTcRowFail,
  tcCheckItem,
  tcIdBadge,
  tcTitle,
  tcInlineActions,
  tcTagBtnPass,
  tcTagBtnPassActive,
  tcTagBtnFail,
  tcTagBtnFailActive,
  mtPriorityBadge,
  mtModeBadge,
  mtProgressStrip,
  mtProgItem,
  mtProgDot,
  mtProgDotPass,
  mtProgDotFail,
  mtProgDotActive,
  mtProgDotPending,
  mtProgId,
  mtProgRemaining,
  mtTcCard,
  mtTcCardHeader,
  mtTcTitle,
  mtTcCaptureStatus,
  mtBtnPass,
  mtBtnFail,
  mtActiveWrap,
  mtRunningIcon,
  mtPulseDot,
  mtPulseDotRed,
  mtActiveTitle,
  mtActiveSub,
  mtSummaryGrid,
  mtSummaryGrid3col,
  mtSummaryCell,
  mtSummaryN,
  mtSummaryL,
  mtTagAskText,
  mtResultBadge,
  mtResultBadgePass,
  mtResultBadgeFail,
} from './manual-testing-layout';

type Screen =
  | 'form'
  | 'coverage-options'
  | 'sess-confirm'
  | 'sess-active'
  | 'sess-tag-ask'
  | 'sess-tag'
  | 'tag-done'
  | 'jira-cases'
  | 'tc-run';

type JiraPageCacheEntry = {
  groups: ManualTestCaseGroup[];
  nextPageToken: string | null;
  isLast: boolean;
};

type JiraTcRef = {
  tc: ManualTestCase;
  suiteId: number;
  suiteName: string;
};

const githubInitSchema = z.object({
  testerName: z.string().min(1, 'Please enter your name or tester ID.'),
  frontendUrl: z
    .string()
    .min(1, 'Please enter a frontend URL.')
    .refine(
      val => z.string().url().safeParse(val).success,
      'Enter a valid URL',
    ),
  referenceId: z.string(),
  featureName: z.string(),
  jiraEmail: z.string().min(1, 'Please enter your JIRA email.'),
  jiraApiToken: z.string().min(1, 'Please enter your JIRA API token.'),
  jiraOrganization: z.string().min(1, 'Please enter your JIRA organization.'),
  jiraProject: z.string().min(1, 'Please enter your JIRA project.'),
});

type GitHubInitFormValues = z.infer<typeof githubInitSchema>;

const GitHubManualTestingView = () => {
  const userSelectedComponent = useSelector(
    (state: RootState) => state.userSelectedComponentSlice,
  );
  const onboardedFrontendUrl = userSelectedComponent.frontendUrl?.trim() ?? '';
  const githubInitFields = useMemo(
    (): FieldConfig<GitHubInitFormValues>[] => [
      {
        name: 'testerName',
        label: 'Tester Name',
        type: 'text',
        placeholder: 'Enter your name',
      },
      {
        name: 'frontendUrl',
        label: 'Frontend URL',
        type: 'text',
        placeholder: onboardedFrontendUrl
          ? undefined
          : 'https://your-app.example.com',
        disabled: Boolean(onboardedFrontendUrl),
      },
      {
        name: 'jiraEmail',
        label: 'Email ID',
        type: 'text',
        placeholder: 'you@company.com',
      },
      {
        name: 'jiraApiToken',
        label: 'API Token',
        type: 'password',
        placeholder: 'Enter your JIRA API token',
      },
      {
        name: 'jiraOrganization',
        label: 'JIRA Organization',
        type: 'text',
        placeholder: 'e.g. your-organization',
      },
      {
        name: 'jiraProject',
        label: 'JIRA Project',
        type: 'text',
        placeholder: 'e.g. your-project',
      },
      {
        name: 'referenceId',
        label: 'Reference ID (optional)',
        type: 'text',
        placeholder: 'e.g. Sprint-24, UAT-May2026',
      },
      {
        name: 'featureName',
        label: 'Feature Name (optional)',
        type: 'text',
        placeholder: 'e.g. Checkout, Login',
      },
    ],
    [onboardedFrontendUrl],
  );
  const [screen, setScreen] = useState<Screen>('form');
  const [apiError, setApiError] = useState('');
  const [jiraGroups, setJiraGroups] = useState<ManualTestCaseGroup[]>([]);
  const [startingTCId, setStartingTCId] = useState<string | null>(null);
  const [currentTC, setCurrentTC] = useState<ManualTestCase | null>(null);
  const [activeSession, setActiveSession] = useState<StartSessionResponse | null>(
    null,
  );
  const [tcResultMap, setTCResultMap] = useState<
    Record<string, 'pass' | 'fail'>
  >({});
  const [sessionData, setSessionData] = useState<GitHubInitFormValues | null>(
    null,
  );
  const [endData, setEndData] = useState<EndSessionResponse | null>(null);
  const [tagSubmitResponse, setTagSubmitResponse] =
    useState<SubmitSessionTestCasesResponse | null>(null);
  const [checkedTCIds, setCheckedTCIds] = useState<Set<string>>(new Set());
  const [tagResultMap, setTagResultMap] = useState<
    Record<string, 'pass' | 'fail'>
  >({});
  const [jiraPageHistory, setJiraPageHistory] = useState<string[]>(['']);
  const [jiraNextPageToken, setJiraNextPageToken] = useState<string | null>(
    null,
  );
  const [jiraIsLast, setJiraIsLast] = useState(true);
  const [jiraPageCache, setJiraPageCache] = useState<
    Record<string, JiraPageCacheEntry>
  >({});
  const [jiraTcById, setJiraTcById] = useState<Record<string, JiraTcRef>>({});
  const [perTcRunId, setPerTcRunId] = useState<string | null>(null);
  const { mutate: startSession, isPending: isStarting } =
    useStartManualSession();
  const { mutate: endSession, isPending: isEnding } = useEndManualSession();
  const { mutate: markSessionResult, isPending: isMarkingTC } =
    useMarkManualSessionResult();
  const { mutate: fetchJiraCases, isPending: isFetchingJira } =
    useFetchTestCasesFromJira();
  const { mutate: submitResults, isPending: isSaving } =
    useSubmitSessionTestCases();

  const allTestCases = jiraGroups.flatMap(s => s.testCases);
  const pendingTCs = allTestCases.filter(tc => !tcResultMap[tc.testCaseId]);

  const mergeJiraLookup = (groups: ManualTestCaseGroup[]) => {
    setJiraTcById(prev => {
      const next = { ...prev };
      for (const suite of groups) {
        for (const tc of suite.testCases) {
          next[tc.testCaseId] = {
            tc,
            suiteId: suite.suiteId,
            suiteName: suite.suiteName,
          };
        }
      }
      return next;
    });
  };

  const applyJiraPage = (pageToken: string, entry: JiraPageCacheEntry) => {
    setJiraGroups(entry.groups);
    setJiraNextPageToken(entry.nextPageToken);
    setJiraIsLast(entry.isLast);
    mergeJiraLookup(entry.groups);
  };

  const resetJiraPagination = () => {
    setJiraPageHistory(['']);
    setJiraNextPageToken(null);
    setJiraIsLast(true);
    setJiraPageCache({});
    setJiraTcById({});
  };

  const buildSessionRequest = (data: GitHubInitFormValues) =>
    buildStartManualSessionRequest({
      testerName: data.testerName,
      frontendAppUrl: resolveManualTestingTargetAppUrl(
        data.frontendUrl,
        onboardedFrontendUrl,
      ),
      scanId: userSelectedComponent.scanId || undefined,
      repoId: userSelectedComponent.dbRepoId || undefined,
      branchId: userSelectedComponent.branchId || undefined,
      referenceId: data.referenceId,
      featureName: data.featureName,
    });

  const { renderFields, onSubmit: handleBeginSession } =
    useOnboardingForm<GitHubInitFormValues>({
      fields: githubInitFields,
      defaultValues: {
        testerName: '',
        frontendUrl: onboardedFrontendUrl,
        referenceId: '',
        featureName: '',
        jiraEmail: '',
        jiraApiToken: '',
        jiraOrganization: '',
        jiraProject: '',
      },
      schema: githubInitSchema,
      onSubmit: data => {
        setApiError('');
        setSessionData(data);
        setScreen('coverage-options');
      },
    });

  const beginSession = (data: GitHubInitFormValues) => {
    setApiError('');
    startSession(buildSessionRequest(data), {
      onSuccess: session => {
        setActiveSession(session);
        window.open(session?.launchUrl, '_blank');
        setScreen('sess-active');
      },
      onError: err => {
        setApiError(
          getApiErrorMessage(
            err,
            'Failed to start session. Please try again.',
          ),
        );
      },
    });
  };

  const handleReset = () => {
    setScreen('form');
    setSessionData(null);
    setActiveSession(null);
    setEndData(null);
    setTagSubmitResponse(null);
    setCheckedTCIds(new Set());
    setTagResultMap({});
    setPerTcRunId(null);
    resetJiraPagination();
    setApiError('');
  };

  const handleToggleCheck = (tcId: string) => {
    setCheckedTCIds(prev => {
      const next = new Set(prev);
      if (next.has(tcId)) next.delete(tcId);
      else next.add(tcId);
      return next;
    });
  };

  const handleSaveTagResults = () => {
    if (!activeSession) {
      handleReset();
      return;
    }
    setApiError('');
    const results = Array.from(checkedTCIds)
      .filter(id => tagResultMap[id])
      .map(id => {
        const ref = jiraTcById[id];
        if (!ref) return null;
        return {
          testCaseId: id,
          name: ref.tc.name,
          suiteId: ref.suiteId,
          suiteName: ref.suiteName,
          priority: ref.tc.priority,
          status: (tagResultMap[id] === 'pass' ? 'passed' : 'failed') as
            | 'passed'
            | 'failed'
            | 'skipped',
          note: null,
        };
      })
      .filter((result): result is NonNullable<typeof result> => result !== null);
    submitResults(
      {
        sessionId: activeSession.sessionId,
        body: { taggingState: 'tagged', results },
      },
      {
        onSuccess: data => {
          setTagSubmitResponse(data);
          setScreen('tag-done');
        },
        onError: err => {
          setApiError(
            getApiErrorMessage(
              err,
              'Failed to save results. Please try again.',
            ),
          );
        },
      },
    );
  };

  const handleEndSession = () => {
    if (!activeSession) return;
    setApiError('');
    endSession(activeSession.sessionId, {
      onSuccess: data => {
        setEndData(data);
        setScreen('sess-tag-ask');
      },
      onError: err => {
        setApiError(
          getApiErrorMessage(err, 'Failed to end session. Please try again.'),
        );
      },
    });
  };

  const loadJiraPage = (
    pageToken: string,
    purpose?: 'tag' | 'per-tc' | null,
  ) => {
    if (!sessionData) return;

    const cached = jiraPageCache[pageToken];
    if (cached) {
      applyJiraPage(pageToken, cached);
      if (purpose === 'tag') {
        setScreen('sess-tag');
      } else if (purpose === 'per-tc') {
        setScreen('jira-cases');
      }
      return;
    }

    setApiError('');
    fetchJiraCases(
      {
        organization: sessionData.jiraOrganization.trim(),
        jiraEmail: sessionData.jiraEmail.trim(),
        jiraApiToken: sessionData.jiraApiToken.trim(),
        project: sessionData.jiraProject.trim(),
        ...(pageToken ? { pageToken } : {}),
      },
      {
        onSuccess: result => {
          const entry: JiraPageCacheEntry = {
            groups: result.groups,
            nextPageToken: result.pageToken ?? null,
            isLast: result.isLast ?? !result.pageToken,
          };
          setJiraPageCache(prev => ({ ...prev, [pageToken]: entry }));
          applyJiraPage(pageToken, entry);
          if (purpose === 'tag') {
            setScreen('sess-tag');
          } else if (purpose === 'per-tc') {
            setScreen('jira-cases');
          }
        },
        onError: err => {
          setJiraGroups([]);
          setApiError(
            getApiErrorMessage(
              err,
              'Failed to fetch test cases from JIRA. Please try again.',
            ),
          );
        },
      },
    );
  };

  const fetchJiraTestCases = (purpose: 'tag' | 'per-tc') => {
    if (!sessionData) return;
    resetJiraPagination();
    if (purpose === 'tag') {
      setCheckedTCIds(new Set());
      setTagResultMap({});
    } else {
      setPerTcRunId(crypto.randomUUID());
    }
    loadJiraPage('', purpose);
  };

  const handleJiraNextPage = () => {
    if (jiraIsLast || !jiraNextPageToken) return;
    const nextToken = jiraNextPageToken;
    setJiraPageHistory(prev => [...prev, nextToken]);
    loadJiraPage(nextToken);
  };

  const handleJiraPreviousPage = () => {
    if (jiraPageHistory.length <= 1) return;
    const nextHistory = jiraPageHistory.slice(0, -1);
    const previousToken = nextHistory[nextHistory.length - 1] ?? '';
    setJiraPageHistory(nextHistory);
    loadJiraPage(previousToken);
  };

  const renderJiraPagination = () => {
    if (jiraPageHistory.length <= 1 && jiraIsLast) return null;
    return (
      <div className={cn(mtBtnRow, 'mt-4')}>
        <Button
          type="button"
          variant="secondary"
          disabled={jiraPageHistory.length <= 1 || isFetchingJira}
          onClick={handleJiraPreviousPage}>
          Previous
        </Button>
        <span className="text-sm text-[#5a5a5a]">
          Page {jiraPageHistory.length}
        </span>
        <Button
          type="button"
          variant="secondary"
          disabled={jiraIsLast || !jiraNextPageToken || isFetchingJira}
          onClick={handleJiraNextPage}>
          {isFetchingJira ? <Spinner compact /> : null}
          Next
        </Button>
      </div>
    );
  };

  const handleStartTCSession = (tc: ManualTestCase) => {
    if (!sessionData || isStarting) return;
    setStartingTCId(tc.testCaseId);
    setApiError('');
    const ref = jiraTcById[tc.testCaseId];
    startSession(
      buildStartManualSessionRequest({
        testerName: sessionData.testerName,
        frontendAppUrl: resolveManualTestingTargetAppUrl(
          sessionData.frontendUrl,
          onboardedFrontendUrl,
        ),
        scanId: userSelectedComponent.scanId || undefined,
        repoId: userSelectedComponent.dbRepoId || undefined,
        branchId: userSelectedComponent.branchId || undefined,
        referenceId: sessionData.referenceId,
        featureName: sessionData.featureName,
        testCase: {
          testCaseId: tc.testCaseId,
          testCaseName: tc.name,
          suiteId: ref?.suiteId,
          suiteName: ref?.suiteName,
          priority: tc.priority || undefined,
          runId: perTcRunId || undefined,
        },
      }),
      {
      onSuccess: session => {
        setStartingTCId(null);
        setCurrentTC(tc);
        setActiveSession(session);
        window.open(session?.launchUrl, '_blank');
        setScreen('tc-run');
      },
      onError: err => {
        setStartingTCId(null);
        setApiError(
          getApiErrorMessage(
            err,
            'Failed to start test case session. Please try again.',
          ),
        );
      },
    },
    );
  };

  const handleMarkTC = (result: 'pass' | 'fail') => {
    if (!activeSession || !currentTC) return;
    setApiError('');
    const sessionId = activeSession.sessionId;
    const status = result === 'pass' ? 'passed' : 'failed';
    markSessionResult(
      { sessionId, body: { status } },
      {
        onSuccess: () => {
          endSession(sessionId, {
            onSuccess: () => {
              setTCResultMap(prev => ({
                ...prev,
                [currentTC.testCaseId]: result,
              }));
              setCurrentTC(null);
              setActiveSession(null);
              setScreen('jira-cases');
            },
            onError: err => {
              setApiError(
                getApiErrorMessage(
                  err,
                  'Failed to end test case session. Please try again.',
                ),
              );
            },
          });
        },
        onError: err => {
          setApiError(
            getApiErrorMessage(
              err,
              'Failed to record test case result. Please try again.',
            ),
          );
        },
      },
    );
  };

  if (screen === 'tc-run' && currentTC && activeSession) {
    return (
      <div className={mtView}>
        <div className={mtProgressStrip}>
          {allTestCases.map(tc => {
            const res = tcResultMap[tc.testCaseId];
            const isCurrent = tc.testCaseId === currentTC.testCaseId;
            return (
              <div key={tc.testCaseId} className={mtProgItem}>
                <span
                  className={cn(
                    mtProgDot,
                    isCurrent
                      ? mtProgDotActive
                      : res === 'pass'
                        ? mtProgDotPass
                        : res === 'fail'
                          ? mtProgDotFail
                          : mtProgDotPending,
                  )}
                />
                <span className={mtProgId}>{tc.testCaseId}</span>
              </div>
            );
          })}
          <div className={mtProgRemaining}>{pendingTCs.length} remaining</div>
        </div>

        <div className={mtTcCard}>
          <div className={mtTcCardHeader}>
            <span className={tcIdBadge}>{currentTC.testCaseId}</span>
            {currentTC.priority ? (
              <span className={mtPriorityBadge}>{currentTC.priority}</span>
            ) : null}
            <div className={mtTcTitle}>{currentTC.name}</div>
          </div>

          <div className={mtTcCaptureStatus}>
            Coverage is being captured for this test case
          </div>

          <p className="mb-2 text-center text-xs text-[#5a5a5a]">
            If coverage is not recording, copy this URL and open it in your
            browser:
          </p>
          <div className="mb-5 flex gap-2">
            <Input readOnly value={activeSession.launchUrl} />
            <Button
              type="button"
              variant="secondary"
              className="shrink-0"
              onClick={() => {
                void navigator.clipboard.writeText(activeSession.launchUrl);
              }}>
              Copy URL
            </Button>
          </div>

          {apiError ? (
            <div className={mtError} role="alert">
              {apiError}
            </div>
          ) : null}

          <div className={mtBtnRow}>
            <Button
              className={mtBtnPass}
              onClick={() => handleMarkTC('pass')}
              disabled={isEnding || isMarkingTC}>
              {isEnding || isMarkingTC ? '…' : 'Pass'}
            </Button>
            <Button
              variant="destructive"
              className={mtBtnFail}
              onClick={() => handleMarkTC('fail')}
              disabled={isEnding || isMarkingTC}>
              {isEnding || isMarkingTC ? '…' : 'Fail'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'jira-cases') {
    return (
      <div className={mtView}>
        <div className={cn(mtBtnRow, mtBtnRowTop)}>
          <Button
            variant="secondary"
            onClick={() => setScreen('coverage-options')}>
            Cancel
          </Button>
        </div>

        <div className={mtPageTitle}>Select a Test Case</div>
        <div className={mtPageSub}>
          Click a test case to open a coverage session for it in a new window.
        </div>

        {apiError ? (
          <div className={mtError} role="alert">
            {apiError}
          </div>
        ) : null}

        {jiraGroups.length === 0 ? (
          <div className={mtPageSub}>
            No test cases were returned for these credentials.
          </div>
        ) : (
          <div className={mtTcTree}>
            {jiraGroups.map(suite => (
              <div key={suite.suiteId} className={mtTcSuite}>
                <div className={mtTcSuiteHeader}>
                  <span>{suite.suiteName}</span>
                  <span className={mtTcSuiteCount}>
                    {suite.testCases.length}
                  </span>
                </div>
                {suite.testCases.map(tc => {
                  const isStartingThis = startingTCId === tc.testCaseId;
                  return (
                    <div
                      key={tc.testCaseId}
                      className={cn(mtTcRow, mtTcRowBtn)}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        !isStarting && handleStartTCSession(tc)
                      }
                      onKeyDown={e =>
                        e.key === 'Enter' &&
                        !isStarting &&
                        handleStartTCSession(tc)
                      }>
                      <div>
                        <span className={tcIdBadge}>{tc.testCaseId}</span>
                        {tc.priority ? (
                          <span className={mtPriorityBadge}>{tc.priority}</span>
                        ) : null}
                      </div>
                      <h3 className={tcTitle}>{tc.name}</h3>
                      {isStartingThis ? (
                        <span className={mtModeBadge}>Starting…</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {renderJiraPagination()}
      </div>
    );
  }

  if (screen === 'sess-active' && activeSession) {
    return (
      <div className={mtView}>
        <div className={mtActiveWrap}>
          <div className={mtRunningIcon}>
            <span className={cn(mtPulseDot, mtPulseDotRed)} />
          </div>
          <div className={mtActiveTitle}>Session is Running</div>
          <div className={mtActiveSub}>
            Use the application in the opened window. Every method triggered is
            being recorded.
          </div>

          <div className="mx-auto mb-5 w-full max-w-lg text-left">
            <div className={mtDetailLabel}>Session URL</div>
            <div className="mt-1 flex gap-2">
              <Input readOnly value={activeSession.launchUrl} />
              <Button
                type="button"
                variant="secondary"
                className="shrink-0"
                onClick={() => {
                  void navigator.clipboard.writeText(activeSession.launchUrl);
                }}>
                Copy
              </Button>
            </div>
            <p className="mt-2 text-xs text-[#5a5a5a]">
              Didn&apos;t the window open? Copy this URL and paste it into a new
              browser tab.
            </p>
          </div>

          {apiError ? (
            <div className={mtError} role="alert">
              {apiError}
            </div>
          ) : null}

          <div className={mtBtnRow}>
            <Button disabled={isEnding} onClick={handleEndSession}>
              {isEnding ? <Spinner compact /> : null}
              End Session
            </Button>
            <Button variant="secondary" onClick={handleReset}>
              Start New Session
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'sess-tag-ask' && endData) {
    const { methodsHit, fileCount } = getEndSessionDisplayStats(endData);

    return (
      <div className={mtView}>
        <div className={mtPageTitle}>Session Ended</div>
        <div className={mtPageSub}>
          Coverage has been saved for this session.
        </div>

        <div className={cn(mtSummaryGrid)}>
          <div className={mtSummaryCell}>
            <div className={mtSummaryN}>{endData.duration}</div>
            <div className={mtSummaryL}>Duration</div>
          </div>
        </div>

        <div className={mtCardWrap}>
          <Card>
            <CardContent className="p-4">
              <div className={mtCardTitle}>Tag Test Cases</div>
              <p className={mtTagAskText}>
                Do you want to tag test cases to this session&apos;s coverage and
                mark pass / fail?
              </p>
            </CardContent>
          </Card>
        </div>

        {apiError ? (
          <div className={mtError} role="alert">
            {apiError}
          </div>
        ) : null}

        <div className={mtBtnRow}>
          <Button
            disabled={isFetchingJira}
            onClick={() => fetchJiraTestCases('tag')}>
            {isFetchingJira ? <Spinner compact /> : null}
            Yes — Tag Test Cases
          </Button>
          <Button variant="secondary" onClick={handleReset}>
            No — Done
          </Button>
        </div>
      </div>
    );
  }

  if (screen === 'sess-tag') {
    return (
      <div className={mtView}>
        <div className={mtPageTitle}>Tag Test Cases</div>
        <div className={mtPageSub}>
          Select the test cases you executed and mark them pass or fail.
        </div>

        <div className={mtTcTree}>
          {jiraGroups.map(suite => (
            <div key={suite.suiteId} className={mtTcSuite}>
              <div className={mtTcSuiteHeader}>
                <span>{suite.suiteName}</span>
                <span className={mtTcSuiteCount}>{suite.testCases.length}</span>
              </div>
              {suite.testCases.map(tc => {
                const isChecked = checkedTCIds.has(tc.testCaseId);
                const result = tagResultMap[tc.testCaseId];
                return (
                  <div
                    key={tc.testCaseId}
                    className={cn(
                      mtTcRow,
                      result === 'pass' && mtTcRowPass,
                      result === 'fail' && mtTcRowFail,
                    )}>
                    <div className={tcCheckItem}>
                      <Checkbox
                        checked={isChecked}
                        onChange={() => handleToggleCheck(tc.testCaseId)}
                        className="shrink-0 items-center [&>button]:mt-0"
                      />
                      <span className={tcIdBadge}>{tc.testCaseId}</span>
                      <span className={tcTitle}>{tc.name}</span>
                      {/* {tc.priority ? (
                        <span className={mtPriorityBadge}>{tc.priority}</span>
                      ) : null} */}
                    </div>
                    {isChecked ? (
                      <div className={tcInlineActions}>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            tcTagBtnPass,
                            result === 'pass' && tcTagBtnPassActive,
                          )}
                          onClick={() =>
                            setTagResultMap(prev => ({
                              ...prev,
                              [tc.testCaseId]: 'pass',
                            }))
                          }>
                          Pass
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            tcTagBtnFail,
                            result === 'fail' && tcTagBtnFailActive,
                          )}
                          onClick={() =>
                            setTagResultMap(prev => ({
                              ...prev,
                              [tc.testCaseId]: 'fail',
                            }))
                          }>
                          Fail
                        </Button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {renderJiraPagination()}

        {apiError ? (
          <div className={mtError} role="alert">
            {apiError}
          </div>
        ) : null}

        <div className={mtBtnRow}>
          <Button disabled={isSaving} onClick={handleSaveTagResults}>
            {isSaving ? <Spinner compact /> : null}
            Save &amp; Done
          </Button>
          <Button variant="secondary" onClick={handleReset}>
            Skip — Done
          </Button>
        </div>
      </div>
    );
  }

  if (screen === 'tag-done' && tagSubmitResponse) {
    const groupedResults = tagSubmitResponse.results.reduce<
      Record<number, { suiteName: string; items: typeof tagSubmitResponse.results }>
    >((acc, r) => {
      if (!acc[r.suiteId]) {
        acc[r.suiteId] = { suiteName: r.suiteName, items: [] };
      }
      acc[r.suiteId].items.push(r);
      return acc;
    }, {});

    const passed = tagSubmitResponse.results.filter(
      r => r.status === 'passed',
    ).length;
    const failed = tagSubmitResponse.results.filter(
      r => r.status === 'failed',
    ).length;

    return (
      <div className={mtView}>
        <div className={mtPageTitle}>Tagging Complete</div>
        <div className={mtPageSub}>
          Test cases have been tagged to this session.
        </div>

        <div className={cn(mtSummaryGrid, mtSummaryGrid3col)}>
          <div className={mtSummaryCell}>
            <div className={cn(mtSummaryN, 'text-[#16a34a]')}>{passed}</div>
            <div className={mtSummaryL}>Test Cases Passed</div>
          </div>
          <div className={mtSummaryCell}>
            <div className={cn(mtSummaryN, 'text-[#dc2626]')}>{failed}</div>
            <div className={mtSummaryL}>Test Cases Failed</div>
          </div>
        </div>

        <div className={mtTcTree}>
          {Object.entries(groupedResults).map(
            ([suiteId, { suiteName, items }]) => (
              <div key={suiteId} className={mtTcSuite}>
                <div className={mtTcSuiteHeader}>
                  <span>{suiteName}</span>
                  <span className={mtTcSuiteCount}>{items.length}</span>
                </div>
                {items.map(r => {
                  const isPass = r.status === 'passed';
                  return (
                    <div
                      key={r.testCaseId}
                      className={cn(
                        mtTcRow,
                        isPass ? mtTcRowPass : mtTcRowFail,
                      )}>
                      <div>
                        <span className={tcIdBadge}>{r.testCaseId}</span>
                        {r.priority ? (
                          <span className={mtPriorityBadge}>{r.priority}</span>
                        ) : null}
                      </div>
                      <h3 className={tcTitle}>{r.name}</h3>
                      <span
                        className={cn(
                          mtResultBadge,
                          isPass ? mtResultBadgePass : mtResultBadgeFail,
                        )}>
                        {isPass ? 'Passed' : 'Failed'}
                      </span>
                    </div>
                  );
                })}
              </div>
            ),
          )}
        </div>

        <div className={mtBtnRow}>
          <Button onClick={handleReset}>Start New Session</Button>
        </div>
      </div>
    );
  }

  if (screen === 'sess-confirm' && sessionData) {
    return (
      <div className={mtView}>
        <div className={cn(mtBtnRow, mtBtnRowTop)}>
          <Button variant="secondary" onClick={() => setScreen('coverage-options')}>
            Back
          </Button>
        </div>

        <div className={mtPageTitle}>Session Coverage</div>

        <div className={mtWarn}>
          <div className={mtWarnText}>
            <strong>
              This captures whole-session coverage — not per test case.
            </strong>
            <br />
            Everything you execute while the session is active is captured as
            one combined record.
          </div>
        </div>

        <div className={mtCardWrap}>
          <Card>
            <CardContent className="p-4">
              <div className={mtCardTitle}>Session Details</div>
              <div className={mtDetailGrid}>
                <div>
                  <div className={mtDetailLabel}>Tester</div>
                  <strong>{sessionData.testerName}</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Mode</div>
                  <strong>Session Coverage</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Repo</div>
                  <strong>{userSelectedComponent.repoName || '—'}</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Branch</div>
                  <strong>{userSelectedComponent.branchName || '—'}</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Frontend URL</div>
                  <strong>
                    {resolveManualTestingTargetAppUrl(
                      sessionData.frontendUrl,
                      onboardedFrontendUrl,
                    ) || '—'}
                  </strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Coverage API</div>
                  <strong>{getManualTestingPlatformBackendUrl()}</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Reference</div>
                  <strong>{sessionData.referenceId.trim() || '—'}</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Feature</div>
                  <strong>{sessionData.featureName.trim() || '—'}</strong>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {apiError ? (
          <div className={mtError} role="alert">
            {apiError}
          </div>
        ) : null}

        <div className={mtBtnRow}>
          <Button
            disabled={isStarting}
            onClick={() => beginSession(sessionData)}>
            {isStarting ? <Spinner compact /> : null}
            Start Session
          </Button>
          <Button variant="secondary" onClick={() => setScreen('coverage-options')}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (screen === 'coverage-options') {
    return (
      <div className={mtView}>
        <div className={cn(mtBtnRow, mtBtnRowTop)}>
          <Button variant="secondary" onClick={() => setScreen('form')}>
            Back
          </Button>
        </div>

        <div className={mtPageTitle}>Start Manual Testing</div>
        <div className={mtPageSub}>Choose how you want to capture coverage.</div>

        {apiError ? (
          <div className={mtError} role="alert">
            {apiError}
          </div>
        ) : null}

        <div className={mtModeGrid}>
          <div className={cn(mtModeCard, mtModeCardActive)}>
            <div className={mtModeTitle}>Record Session Coverage</div>
            <div className={mtModeDesc}>
              Capture coverage for your whole session in one go. Optionally tag
              test cases and mark pass/fail at the end.
            </div>
            <div className={mtBtnRow}>
              <Button
                type="button"
                disabled={!sessionData}
                onClick={() => setScreen('sess-confirm')}>
                Continue
              </Button>
            </div>
          </div>

          <div className={cn(mtModeCard, mtModeCardActive)}>
            <div className={mtModeTitle}>Record per Test Case</div>
            <div className={mtModeDesc}>
              Run JIRA test cases one by one. Coverage is mapped to each test
              case. Mark pass or fail after each and move to the next.
            </div>
            <div className={mtBtnRow}>
              <Button
                type="button"
                variant="secondary"
                disabled={isFetchingJira || !sessionData}
                onClick={() => fetchJiraTestCases('per-tc')}>
                {isFetchingJira ? <Spinner compact /> : null}
                Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={mtView}>
      <div className={mtPageTitle}>Start Manual Testing Session</div>
      <div className={mtPageSub}>
        Enter your details to begin. These are used for this session only.
      </div>

      <div className={mtCardWrap}>
        <Card>
          <CardContent className="p-4">
            <div className={mtCardTitle}>Session Details</div>
            <form onSubmit={handleBeginSession}>
              {renderFields()}
              {apiError ? (
                <div className={mtError} role="alert">
                  {apiError}
                </div>
              ) : null}
              <div className={mtBtnRow}>
                <Button type="submit" disabled={isStarting}>
                  {isStarting ? <Spinner compact /> : null}
                  Begin Session
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GitHubManualTestingView;
